import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { IGliderItem } from '../../atoms/glider-item/glider-item';
import useIntersect from '../../../hooks/useIntersect';
import t from '../../../labels/labelling-utils';
import ErrorComponent from '../../atoms/error/error';
import Spacer from '../../atoms/spacer/spacer';
import ReportsHomeTable, { IReportsHomeTable } from '../../organisms/reports-home-table/reports-home-table';
import ErrorBoundary from '../error-boundary/error-boundary';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import { GliderWrapper, IMenuGliderItems, MenuGliderItems } from '../../organisms';
import NoSSR from '../../../context/ssr/no-ssr';
import { IIcon } from '../../atoms/icons/icon';
import IconArrowLeft from '../../atoms/icons/icon-arrow-left';
import IconArrowRight from '../../atoms/icons/icon-arrow-right';

export interface IMatchReportsHome {
  matchDayList: Array<IReportsHomeTable>;
  gliderItems: Array<IGliderItem>;
}

const classNames = (): IClassNames =>
  makeClassNames({
    gliderWrapper: 'match-reports-home--wrapper',
    matchReportsHome: 'match-reports-home',
  });

const iconLeft = (): React.ReactElement<IIcon> => <IconArrowLeft size="xxxsmall" color="blue" />;
const iconRight = (): React.ReactElement<IIcon> => <IconArrowRight size="xxxsmall" color="blue" />;

const ssrPlaceholder = (props: IMenuGliderItems, cn: IClassNames) => {
  return (
    <div className={cn.gliderWrapper}>
      <MenuGliderItems {...props} isSSR />
    </div>
  );
};

const createId = (i: string | number): string => `giornata-${i}`;

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

const isValidMatchList = (matchDayList: IReportsHomeTable[]) => Boolean(matchDayList && matchDayList.length);

const MatchReportsHome: React.FC<IMatchReportsHome> = ({ matchDayList, gliderItems }) => {
  const cn = classNames();
  const [activeIndex, setActiveIndex] = useState(matchDayList.length - 1);

  const onIntersect = (item: HTMLElement) => {
    setActiveIndex(matchDayList.length - Number(item.dataset.index) - 1);
  };
  useIntersect('[data-intersect]', onIntersect);

  const onSelectedGliderItem = (gliderIndex: number) => {
    if (gliderIndex !== activeIndex) {
      setActiveIndex(gliderIndex);
    }
  };

  const gliderComponentProps: IMenuGliderItems = {
    items: gliderItems,
    activeSlideIndex: activeIndex,
    variant: 'mgv',
    onItemSelectedCallback: onSelectedGliderItem,
  };

  return (
    <div className={cn.matchReportsHome}>
      <ErrorBoundary hasError={!isValidMatchList(matchDayList)} errorComponent={getErrorComponent()}>
        <React.Fragment>
          <NoSSR placeholder={ssrPlaceholder(gliderComponentProps, cn)}>
            <GliderWrapper
              propsComponent={gliderComponentProps}
              Component={MenuGliderItems}
              itemWidth={66}
              duration={2}
              bordered="top-bottom"
              iconLeft={iconLeft()}
              iconRight={iconRight()}
              hasArrows
              scrollLock
              exactWidth
              topOffset
            />
          </NoSSR>
          <Spacer size={30} />
          {matchDayList.map((matchDay: IReportsHomeTable) => {
            const id = createId(matchDay.round);
            return (
              <div id={id} key={id} data-intersect>
                <ReportsHomeTable {...matchDay} round={`${t('match.reports.home.match.day')} ${matchDay.round}`} />
              </div>
            );
          })}
        </React.Fragment>
      </ErrorBoundary>
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  MatchReportsHome.propTypes = {
    matchDayList: PropTypes.arrayOf(PropTypes.shape(ReportsHomeTable.propTypes!).isRequired).isRequired,
  };
}

export default MatchReportsHome;
